<template>
    <main class="">
      <div class="box-border flex items-center gap-2 w-full bg-brand-secondary py-2 px-3 md:px-6" v-if="items.length > 0 && !dismissed">
        <!-- Mirrors the dismiss button's width so the text stays optically centred. -->
        <div class="w-6 shrink-0" aria-hidden="true"></div>
        <transition name="slide-fade" mode="out-in">
          <!-- eslint-disable-next-line vue/no-v-html -- sanitized with DOMPurify -->
          <h2 class="flex-1 min-w-0 text-center text-white font-poppins text-xs" :key="currentItem" v-html="sanitizedCurrentItem">
</h2>
        </transition>
        <button
          type="button"
          class="w-6 shrink-0 flex items-center justify-center p-1 leading-none text-white bg-transparent border-0 cursor-pointer opacity-80 hover:opacity-100"
          aria-label="Dismiss announcement"
          @click="dismiss"
        >
          <XIcon class="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </main>
</template>

<script lang='ts' setup>
import { computed, defineProps, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import DOMPurify from 'dompurify';
import { XIcon } from '@heroicons/vue/outline';

const props = defineProps<{
  announcement: string
}>();

// How long each announcement stays on screen before the ticker advances.
const rotationMs = 5000;

const store = useStore();

/**
 * The brand's own announcement first, then whatever the outage api returned.
 * Blank entries are skipped so they can't rotate in as an empty banner.
 */
const items = computed<string[]>(() => {
  const bodies = store.state.announcements
    .map((announcement: { body: string | null }) => announcement.body);

  return [props.announcement, ...bodies]
    .filter((body: string | null): body is string => Boolean(body?.trim()));
});

const index = ref(0);

// Falls back to the first item so a shrinking list can't render a hole.
const currentItem = computed(() => items.value[index.value] ?? items.value[0] ?? '');

// Announcement bodies come from the outage api, so sanitize before v-html.
const sanitizedCurrentItem = computed(() => DOMPurify.sanitize(currentItem.value));

let timer: number | undefined;

const stop = () => {
  if (timer) {
    window.clearInterval(timer);
    timer = undefined;
  }
};

const start = () => {
  stop();

  // A single announcement has nothing to rotate to.
  if (items.value.length > 1) {
    timer = window.setInterval(() => {
      index.value = (index.value + 1) % items.value.length;
    }, rotationMs);
  }
};

// Announcements arrive after mount, so restart the ticker when they land.
watch(items, () => {
  index.value = 0;
  start();
});

// Dismissal lasts for the session. The banner lives outside router-view, so it
// isn't remounted on navigation and won't reappear as the user moves around.
const dismissed = ref(false);

const dismiss = () => {
  dismissed.value = true;
  stop();
};

onMounted(start);
onBeforeUnmount(stop);

</script>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.4s ease;
}
.slide-fade-enter-from {
  transform: translateX(100px);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateX(-100px);
  opacity: 0;
}
</style>
